import { useEffect, useSyncExternalStore } from 'react';
import { loadNikkeCatalog } from '../api/nikkeCatalogApi.js';

const EMPTY_CATALOG = { items: [], byKey: new Map(), ambiguousKeys: new Map() };
const listeners = new Set();

let snapshot = {
  catalog: null,
  loading: false,
  error: '',
  source: '',
  warnings: [],
};
let activePromise = null;
let forceRefreshQueued = false;
let changeListenerAttached = false;

function emit(next) {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((listener) => listener());
}

function errorMessage(error) {
  return error instanceof Error ? error.message : '니케 DB를 불러오지 못했습니다.';
}

function attachCatalogChangeListener() {
  if (changeListenerAttached || typeof window === 'undefined') return;
  changeListenerAttached = true;
  window.addEventListener('dororaid:nikke-catalog-changed', () => {
    preloadNikkeCatalog({ forceRefresh: true }).catch(() => {});
  });
}

function runCatalogLoad(forceRefresh = false) {
  attachCatalogChangeListener();

  if (!forceRefresh && snapshot.catalog) return Promise.resolve(snapshot.catalog);

  if (activePromise) {
    if (forceRefresh) forceRefreshQueued = true;
    return activePromise;
  }

  emit({ loading: true, error: '' });

  const promise = loadNikkeCatalog({ forceRefresh })
    .then((result) => {
      const catalog = result?.lookup || EMPTY_CATALOG;
      emit({
        catalog,
        loading: false,
        error: '',
        source: String(result?.source || ''),
        warnings: Array.isArray(result?.warnings) ? result.warnings : [],
      });
      return catalog;
    })
    .catch((error) => {
      emit({ loading: false, error: errorMessage(error) });
      throw error;
    })
    .finally(() => {
      if (activePromise === promise) activePromise = null;
      if (forceRefreshQueued) {
        forceRefreshQueued = false;
        runCatalogLoad(true).catch(() => {});
      }
    });

  activePromise = promise;
  return promise;
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return snapshot;
}

export function preloadNikkeCatalog(options = {}) {
  return runCatalogLoad(Boolean(options.forceRefresh));
}

export function refreshNikkeCatalog() {
  return runCatalogLoad(true);
}

export function getNikkeCatalogSnapshot() {
  return snapshot;
}

export function useNikkeCatalog({ enabled = true } = {}) {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (!enabled) return;
    preloadNikkeCatalog().catch(() => {});
  }, [enabled]);

  if (!enabled) return { ...state, loading: false, error: '' };
  return state;
}
